import { useMemo, useEffect } from "react";
import Pages from "./pages/Pages";
import Category from "./components/Category";
import { BrowserRouter } from "react-router-dom";
import Search from "./components/Search";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { GiKnifeFork } from "react-icons/gi";
import { FaTree } from "react-icons/fa";
import { TbLeaf } from "react-icons/tb";

// Helper to get Thanksgiving date (4th Thursday of November)
const getThanksgivingDate = (year) => {
  const november = new Date(year, 10, 1);
  const dayOfWeek = november.getDay();
  const firstThursday = dayOfWeek <= 4 ? 5 - dayOfWeek : 12 - dayOfWeek;
  return new Date(year, 10, firstThursday + 21);
};

// Check if current date is within a holiday week
const getHolidayMode = () => {
  const now = new Date();
  const year = now.getFullYear();

  // Thanksgiving week
  const thanksgiving = getThanksgivingDate(year);
  const thanksgivingStart = new Date(thanksgiving);
  thanksgivingStart.setDate(thanksgiving.getDate() - 3);
  const thanksgivingEnd = new Date(thanksgiving);
  thanksgivingEnd.setDate(thanksgiving.getDate() + 3);

  if (now >= thanksgivingStart && now <= thanksgivingEnd) {
    return 'thanksgiving';
  }

  // Christmas week: Dec 20 - Dec 26
  const christmasStart = new Date(year, 11, 20);
  const christmasEnd = new Date(year, 11, 26);

  if (now >= christmasStart && now <= christmasEnd) {
    return 'christmas';
  }

  return 'default';
};

const HolidayIcon = ({ mode }) => {
  if (mode === 'thanksgiving') return <TbLeaf />;
  if (mode === 'christmas') return <FaTree />;
  return <GiKnifeFork />;
};

// SVG favicons for each holiday mode
const getFaviconSvg = (mode) => {
  const configs = {
    thanksgiving: {
      bg: '#D97706',
      // Leaf icon path
      icon: `<path d="M17 8C8 10 5.9 16.17 3.82 21.34l1.89.66l.95-2.3c.48.17.98.3 1.34.3C19 20 22 3 22 3c-1 2-8 2.25-13 3.25S2 11.5 2 13.5s1.75 3.75 1.75 3.75C7 8 17 8 17 8z" fill="white"/>`,
    },
    christmas: {
      bg: '#16A34A',
      // Tree icon path
      icon: `<path d="M12 2L4 14h4v8h8v-8h4L12 2z" fill="white"/>`,
    },
    default: {
      bg: '#E11D48',
      // Fork and knife icon path
      icon: `<path d="M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z" fill="white"/>`,
    },
  };

  const config = configs[mode] || configs.default;

  return `data:image/svg+xml,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32">
      <rect width="24" height="24" rx="4" fill="${config.bg}"/>
      <g transform="scale(0.7) translate(5, 5)">${config.icon}</g>
    </svg>
  `)}`;
};

function App() {
  const holidayMode = useMemo(() => getHolidayMode(), []);

  // Update favicon based on holiday mode
  useEffect(() => {
    const favicon = document.querySelector("link[rel='icon']");
    if (favicon) {
      favicon.href = getFaviconSvg(holidayMode);
    }
  }, [holidayMode]);

  return (
    <div className="App">
      <BrowserRouter basename="/recipe-react">
        <div className="topDiv">
          <Nav>
            <LogoLink to={"/"}>
              <IconWrapper $holiday={holidayMode}>
                <HolidayIcon mode={holidayMode} />
              </IconWrapper>
              <Logo>Yummies</Logo>
            </LogoLink>
          </Nav>
          <Search />
          <Category />
        </div>

        <Pages />
      </BrowserRouter>
    </div>
  );
}

const Nav = styled.nav`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--spacing-sm) 0;
`

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  text-decoration: none;
  transition: opacity var(--transition-fast);

  &:hover {
    opacity: 0.8;
  }
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: var(--radius-sm);
  background: ${props =>
    props.$holiday === 'thanksgiving' ? '#D97706' :
    props.$holiday === 'christmas' ? '#16A34A' :
    'var(--color-accent)'};
  color: white;

  svg {
    font-size: 1rem;
  }
`

const Logo = styled.span`
  font-size: 1.25rem;
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  letter-spacing: -0.03em;
`

export default App;
